/**
 * Helper functions for merchant details
 * Note: Some functions (getStoreDomain, extractLocationData, getDomainToFetch) 
 * are now in helpers.js and re-exported here for backward compatibility
 */

// Import shared helpers from helpers.js for use in this file
import { getStoreDomain, extractLocationData, getDomainToFetch } from './helpers';

// Re-export shared helpers from helpers.js for backward compatibility
export { getStoreDomain, extractLocationData, getDomainToFetch };

/**
 * Determines app block status text
 */
export const getAppBlockStatus = (storefront, app_block) => {
    if (storefront === '3.0') return "Not Access #";
    if (app_block === 0) return "Storefront 2.0#";
    if (app_block === 1) return "Storefront 1.0 # App block on Cart Page";
    if (app_block === 2) return "Storefront 1.0#App block on Product Page";
    return "Storefront 1.0 # App block on both Cart and Product Page";
};

/**
 * Determines effective store status
 */
export const getEffectiveStoreStatus = (storeStatus, store_details) => {
    return storeStatus || ((store_details?.install_status === 3 || store_details?.is_active) ? 'active' : 'inactive');
};


/**
 * Creates action button handler for metafield updates
 */
export const createMetafieldHandler = (updateFn, successMessage) => {
    return async (shop, metafield_key, metafield_value, setLoading, notifyError, notifySuccess) => {
        setLoading(true);
        const success = await updateFn(shop, metafield_key, metafield_value, notifyError);
        if (success) notifySuccess(successMessage);
        setLoading(false);
    };
};

/**
 * Creates initial state for merchant details component
 * @returns {Object} Initial state object
 */
export const getInitialMerchantState = () => ({
    app_embed: "disabled",
    store_name: "",
    store_domain: "",
    store_logo: "",
    app_block: "",
    storefront: '3.0',
    dropdownOpen: false,
    coupon_list: { columns: [], rows: [] },
    storeStatus: "active",
    isLoading: false,
    isCouponLoading: false,
    variable_charge: 2,
    fixed_charge: 0,
    campaign_variable_charge: 2,
    campaign_fixed_charge: 0,
    store_details: {},
    totalRevenue: 0,
    campaignRevenue: 0,
    couponBlockerRevenue: 0,
    smartDiscountRevenue: 0,
    veeperROI: 0,
    recoveredRevenue: 0,
    uniqueCouponBlocked: 0,
    totalCouponsBlocked: 0,
    totalOrderProtected: 0,
    ratioOfOrdersProtected: 0,
    recoveredRevenuePerOrder: 0,
    revenueFromProtectedOrders: 0,
    hasError: false,
    blocked_orders: { columns: [], rows: [] },
    isBlockedOrderLoading: false,
    plan: "",
    plan_status: "",
    storefrontToken: '',
    cartLocation: '',
    total_fees: 0,
    install_status: 3,
    is_shopify_plus: false,
    current_orders: 0,
});

/**
 * Timeline filter options
 */
export const TIMELINE_OPTIONS = [
    { value: 'ALL', label: 'All' },
    { value: 'THIS_MONTH', label: 'Current Month' },
    { value: 'LAST_MONTH', label: 'Last Month' },
    { value: 'THIS_YEAR', label: 'Current Year' },
    { value: 'LAST_YEAR', label: 'Last Year' },
];

/**
 * Charge field keys
 */
export const CHARGE_FIELDS = ['variable_charge', 'fixed_charge', 'campaign_variable_charge', 'campaign_fixed_charge'];

/**
 * Processes charge values from store details and overrides
 */
export const processChargeValues = (store_details, overrides) => {
    return CHARGE_FIELDS.reduce((acc, key) => {
        acc[key] = Number(overrides[key] || store_details[key] || 0);
        return acc;
    }, {});
};

/**
 * Gets store field value with fallback
 */
export const getStoreField = (store_details, overrides, field, defaultValue = "") => {
    return overrides[field] || store_details[field] || defaultValue;
};

/**
 * Builds state updates for initializeFromData
 */
export const buildInitializationState = (store_details, storeStatus, overrides) => {
    const store_domain = getStoreDomain(store_details, overrides);
    const app_block_status = getAppBlockStatus(store_details.storefront, overrides.app_block_status || store_details.app_block);
    const effectiveStoreStatus = getEffectiveStoreStatus(storeStatus, store_details);
    const charges = processChargeValues(store_details, overrides);

    const fields = ['store_name', 'store_logo', 'discount_type', 'max_discount', 'placement', 'plan', 'plan_status'];
    const fieldValues = fields.reduce((acc, field) => {
        acc[field] = getStoreField(store_details, overrides, field);
        return acc;
    }, {});

    return {
        store_details: { ...store_details },
        ...fieldValues,
        store_domain,
        app_block: app_block_status,
        storeStatus: effectiveStoreStatus,
        ...charges,
        isLoading: true,
        isCouponLoading: true,
        isBlockedOrderLoading: true,
        cartLocation: JSON.stringify(overrides.cart_location || store_details.cart_location),
        publicAccessToken: overrides.public_access_token || store_details.public_access_token || "",
        total_fees: Number(store_details.total_fees) || Number(store_details.plan_info?.price) || 0,
        is_shopify_plus: !!store_details.is_shopify_plus,
    };
};

